/**
 * The BFF client. Deliberately thin: it exists so the state machine can switch on an errorCode
 * instead of on an HTTP status, because two different codes on the same status mean two different
 * screens (401 PAT_REQUIRED and 401 PAT_REJECTED are both "reopen the service", but 403
 * TENANT_NOT_ONBOARDED and 502 ORIGIN_INCOMPLETE say entirely different things to the officer).
 */

export class ApiError extends Error {
  constructor({ status, errorCode, correlationId, message, detail }) {
    super(message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.correlationId = correlationId;
    // ProblemDetails' own "detail" -- a status-specific sentence some 4xxs carry (409
    // CANNOT_CANCEL among them). Kept as its OWN field, deliberately not folded into `message`:
    // `message` is never nullish (the super() call above always falls back to "Request failed
    // (status)"), so a caller writing `error.message ?? fallback` would never see its fallback
    // fire, and would render "Request failed (409)" to a third-party lender's screen the one time
    // the BFF sent no detail. `detail` stays undefined when the body has none, so `error.detail ??
    // fallback` behaves as written. Nothing reads this generically -- see the call site in
    // app.js's CANNOT_CANCEL toast for the one place it reaches a screen, and why that is safe.
    this.detail = typeof detail === 'string' ? detail : undefined;
  }
}

// The same 15 s the host adapter waits on Encompass. fetch has no timeout of its own, so without
// this a BFF that accepts the connection and then never answers leaves the state machine on the
// spinner for ever -- and the spinner branch renders no controls, not even Close.
const DEFAULT_TIMEOUT_MS = 15000;

export async function loadOriginContext(
  { baseUrl, fetch: fetchImpl = globalThis.fetch, timeoutMs = DEFAULT_TIMEOUT_MS },
  { originId, partnerAccessToken },
) {
  return request(fetchImpl, `${baseUrl}/epc/ui/origin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${partnerAccessToken}`,
    },
    body: JSON.stringify({ originId }),
  }, timeoutMs);
}

export async function loadOrder(
  { baseUrl, fetch: fetchImpl = globalThis.fetch, timeoutMs = DEFAULT_TIMEOUT_MS },
  { transactionId, partnerAccessToken },
) {
  return request(fetchImpl, `${baseUrl}/epc/ui/orders/${encodeURIComponent(transactionId)}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${partnerAccessToken}` },
  }, timeoutMs);
}

/** One BFF round trip with the timeout and the error mapping above. Exported so orders.js can
 *  make the order calls the same way without a second copy of the ApiError rules. */
export async function request(fetchImpl, url, init, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await send(fetchImpl, url, init, controller);
  } finally {
    clearTimeout(timer);
  }
}

async function send(fetchImpl, url, init, controller) {
  let response;
  try {
    response = await fetchImpl(url, { ...init, signal: controller.signal });
  } catch (cause) {
    // fetch rejects with TypeError for a dropped connection, a DNS failure AND a blocked CORS
    // preflight. The officer cannot tell those apart and neither can we from here, so they share
    // one screen -- status 0 is what marks "never got an answer". An abort is split out only
    // because TIMEOUT is the one of those where trying again is genuinely likely to work.
    throw new ApiError({
      status: 0,
      errorCode: controller.signal.aborted ? 'TIMEOUT' : 'NETWORK',
      message: cause.message,
    });
  }

  const correlationId = response.headers.get('x-correlation-id') ?? undefined;

  if (!response.ok) {
    // An error body that is not JSON -- an empty 502 from a gateway, an HTML error page -- still
    // has to become an ApiError the state machine can act on, so the parse failure is absorbed
    // HERE and only here. errorCode falls back to UNKNOWN rather than the body escaping raw.
    const body = await response.json().catch(() => ({}));
    throw new ApiError({
      status: response.status,
      errorCode: body.errorCode ?? 'UNKNOWN',
      correlationId,
      detail: body.detail,
    });
  }

  // A 2xx whose body will not parse is NOT a success with an empty object. Returning {} here
  // would hand the loan card a context with no loan in it, and the failure would surface later as
  // a TypeError inside a component that has no error state for it -- a spinner that never resolves.
  // Make it the ApiError the officer's screen can show.
  try {
    return await response.json();
  } catch {
    throw new ApiError({
      status: response.status,
      errorCode: 'MALFORMED_RESPONSE',
      correlationId,
    });
  }
}
